import { useEffect, useRef } from "react";
import { Compass, Brain, Sparkles, Rocket, Users, LineChart, TrendingUp } from "lucide-react";

const STEPS = [
  { n: "01", title: "Discover",       desc: "Tear apart your project — pricing, USP, micro-market. No deck. Real data.", Icon: Compass },
  { n: "02", title: "Strategize",     desc: "Define buyer (end-user / investor / NRI) and the hook that stops the scroll.", Icon: Brain },
  { n: "03", title: "Create",         desc: "RERA-safe creative, microsites, reels, WhatsApp flows — built to convert.", Icon: Sparkles },
  { n: "04", title: "Launch",         desc: "Funnel goes live in 7–10 days. Daily creative tests, hourly CPL watch.", Icon: Rocket },
  { n: "05", title: "Generate Leads", desc: "Site-visit-ready buyers routed in under 60 seconds to your sales team.", Icon: Users },
  { n: "06", title: "Optimize",       desc: "Weekly war-rooms, dashboards, A/B tests. CPL drops week-on-week.", Icon: LineChart },
  { n: "07", title: "Scale",          desc: "Double-down on winning channels until the last unit books. Then we celebrate.", Icon: TrendingUp },
];

export default function Process() {
  const trackRef = useRef(null);

  // Auto-scroll the caterpillar on mobile when in view
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let stepIdx = 0;
    let paused = false;
    let inView = false;

    const isMobile = () => window.innerWidth < 768;

    const io = new IntersectionObserver(
      ([entry]) => { inView = entry.isIntersecting; },
      { threshold: 0.2 }
    );
    io.observe(track);

    const pause = () => { paused = true; };
    const resume = () => { paused = false; };
    track.addEventListener("touchstart", pause, { passive: true });
    track.addEventListener("touchend", resume, { passive: true });
    track.addEventListener("mouseenter", pause);
    track.addEventListener("mouseleave", resume);

    const interval = setInterval(() => {
      if (!isMobile() || !inView || paused) return;
      const items = track.querySelectorAll('[data-testid^="process-step-"]');
      if (!items.length) return;
      stepIdx = (stepIdx + 1) % items.length;
      const target = items[stepIdx];
      track.scrollTo({ left: target.offsetLeft - 16, behavior: "smooth" });
    }, 2400);

    return () => {
      clearInterval(interval);
      io.disconnect();
      track.removeEventListener("touchstart", pause);
      track.removeEventListener("touchend", resume);
      track.removeEventListener("mouseenter", pause);
      track.removeEventListener("mouseleave", resume);
    };
  }, []);

  return (
    <section data-testid="process-section" className="relative py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      {/* 3D wireframe background */}
      <div className="absolute inset-0 pointer-events-none opacity-50">
        <svg className="absolute -top-20 left-1/2 -translate-x-1/2 w-[1400px] h-[700px]" viewBox="0 0 1400 700" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="wire" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#d4ff4f" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#d4ff4f" stopOpacity="0" />
            </linearGradient>
          </defs>
          {Array.from({ length: 16 }).map((_, i) => (
            <line key={`h-${i}`} x1="0" y1={350 + (i - 8) * 50} x2="1400" y2={350 + (i - 8) * 50}
              stroke="url(#wire)" strokeWidth="1" opacity={1 - Math.abs(i - 8) / 8} />
          ))}
          {Array.from({ length: 24 }).map((_, i) => {
            const x = (i / 23) * 1400;
            return (
              <line key={`v-${i}`} x1={x} y1="0" x2={700 + (x - 700) * 0.3} y2="700"
                stroke="url(#wire)" strokeWidth="1" opacity="0.4" />
            );
          })}
        </svg>
        <div className="absolute top-10 right-10 w-20 h-20 border border-[#d4ff4f]/30 blob" style={{ transform: "rotate(15deg)" }} />
        <div className="absolute bottom-20 left-20 w-12 h-12 border border-[#d4ff4f]/40 blob" style={{ animationDelay: "-4s", transform: "rotate(45deg)" }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="mb-14 max-w-3xl">
          <div className="font-mono text-xs text-[#d4ff4f] mb-3 tracking-[0.2em]">#उड़ान</div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/40 mb-4">/ Our Process</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            From onboarding<br />to <span className="italic text-[#d4ff4f]">sold-out.</span>
          </h2>
          <p className="text-white/60 mt-6 max-w-2xl leading-relaxed">
            A 7-step operating system honed across <span className="text-[#d4ff4f]">500+ marketed projects</span>. Predictable. Documented. Repeatable.
          </p>
        </div>

        {/* CATERPILLAR – horizontal scrollable track on mobile (auto-runs), full chain on desktop */}
        <div className="relative" data-testid="process-caterpillar">
          {/* Glow track line */}
          <div className="absolute top-7 left-7 right-7 h-1 bg-gradient-to-r from-[#d4ff4f]/40 via-[#d4ff4f] to-[#d4ff4f]/40 rounded-full opacity-60 hidden md:block" />
          {/* Traveling dot on track */}
          <div className="absolute top-[22px] left-7 right-7 h-3 hidden md:block">
            <div className="caterpillar-dot absolute top-0 w-3 h-3 rounded-full bg-[#d4ff4f] shadow-[0_0_18px_#d4ff4f]" />
          </div>

          <div
            ref={trackRef}
            className="flex md:grid md:grid-cols-7 gap-4 sm:gap-2 overflow-x-auto no-scrollbar pb-3 -mx-5 px-5 md:mx-0 md:px-0 scroll-smooth snap-x snap-mandatory md:snap-none"
          >
            {STEPS.map((s, i) => {
              const Icon = s.Icon;
              return (
                <div
                  key={i}
                  data-testid={`process-step-${i}`}
                  className="relative shrink-0 w-[220px] md:w-auto group caterpillar-seg"
                  style={{ animationDelay: `${i * 0.15}s` }}
                >
                  <div className="relative">
                    <div className="w-14 h-14 rounded-2xl border-2 border-[#d4ff4f]/40 bg-[#0a0b08] flex items-center justify-center text-[#d4ff4f] group-hover:bg-[#d4ff4f] group-hover:text-black transition relative z-10 shadow-[0_0_20px_rgba(212,255,79,0.15)]">
                      <Icon className="w-5 h-5" />
                    </div>
                    {/* Connector arrow between segments */}
                    {i < STEPS.length - 1 && (
                      <span className="hidden md:flex absolute top-1/2 -translate-y-1/2 -right-4 lg:-right-2 text-[#d4ff4f]/60 text-xl">›</span>
                    )}
                  </div>
                  <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-[#d4ff4f]">Step {s.n}</div>
                  <h3 className="font-display text-lg tracking-tight mt-1">{s.title}</h3>
                  <p className="text-xs text-white/60 leading-relaxed mt-2">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Arrow flow text */}
        <div className="mt-10 hidden lg:flex items-center justify-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 flex-wrap" data-testid="process-flow-text">
          {STEPS.map((s, i) => (
            <span key={i} className="inline-flex items-center gap-2">
              <span>{s.title}</span>
              {i < STEPS.length - 1 && <span className="text-[#d4ff4f]">→</span>}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
